/**
 * Decides whether a signer's location falls within a petition's geographic scope: for each of
 * country / state / city the petition specifies (non-blank), the signer's matching field must be
 * equal. Blank petition fields act as wildcards, and state is ignored for non-US petitions.
 *
 * All fields are compared case-insensitively and trimmed. City and state need it because the signer's
 * address does not always arrive as the Google Places canonical value: it can come from the manual
 * free-text field, the saved profile, or geoip, where casing and whitespace vary. State matters
 * because local petitions can be state-level (city blank). Country codes are already canonical, so
 * normalizing them the same way just keeps the comparison uniform. This only recovers genuine matches;
 * distinct cities, state codes, and countries still have distinct normalized values.
 */

type ScopeLocation = Readonly<{
	countryCode?: string | null;
	stateCode?: string | null;
	city?: string | null;
}>;

export const INFERENCE_MATCH_METHOD = 'inference_match' as const;

/**
 * A petition's primary decision maker: the first decision maker of the first legislative-body group.
 * There is no "primary" flag in the schema, so the position is the selection.
 */
export type DecisionMakerReference = Readonly<{
	id: string;
	displayName: string;
}>;

export type ConstituentIdentification = Readonly<{
	isConstituent: boolean;
	method: typeof INFERENCE_MATCH_METHOD;
}>;

const isBlank = (value?: string | null): boolean => value == null || value.trim() === '';

const normalize = (value?: string | null): string => (value ?? '').trim().toLowerCase();

function fieldMatches(petitionValue?: string | null, signerValue?: string | null): boolean {
	const signer = normalize(signerValue);
	return signer !== '' && normalize(petitionValue) === signer;
}

/**
 * True when `signer` falls within the petition's geographic scope. `petition` is the petition's
 * relevant_location; `signer` is the signer's location (from their submitted address or geoip).
 */
function isSignerInPetitionScope(petition: ScopeLocation, signer: ScopeLocation): boolean {
	const fields: ReadonlyArray<keyof ScopeLocation> =
		petition.countryCode === 'US' ? ['countryCode', 'stateCode', 'city'] : ['countryCode', 'city'];

	return fields.every((field) => isBlank(petition[field]) || fieldMatches(petition[field], signer[field]));
}

/**
 * Identifies the signer as a constituent (or not) by scope match. Stores only the decision; the
 * consuming screen reads any decision-maker detail it needs from the petition it already loads.
 */
export function identifyConstituentByScope(petition: ScopeLocation, signer: ScopeLocation): ConstituentIdentification {
	return {
		isConstituent: isSignerInPetitionScope(petition, signer),
		method: INFERENCE_MATCH_METHOD,
	};
}
